import React, { useEffect, useState, useRef } from 'react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { SocialProofSection } from './components/SocialProofSection';
import { StepsSection } from './components/StepsSection';
import { SplitSection } from './components/SplitSection';
import { DifferenceSection } from './components/DifferenceSection';
import { WaitlistSection } from './components/WaitlistSection';
import { Footer } from './components/Footer';
import { ElephantMascot } from './components/ElephantMascot';
import { Button } from './components/common/Button';
import { Star, Circle, Triangle } from 'lucide-react';
export function App() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showWaitlistMessage, setShowWaitlistMessage] = useState(false);
  useEffect(() => {
    document.title = 'Kraya';
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect(); // Only trigger once
      }
    }, {
      threshold: 0.6 // Wait until the section is 60% visible (more centered) before triggering
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white text-[#1A1A1A] font-sans selection:bg-[#FFFC00] selection:text-[#1A1A1A]" data-id="element-0" data-mp="2zllcouynw8jk">
      <Header data-id="element-1" data-mp="33nq4do5juv3k" />
      <main data-id="element-2" data-mp="33n6yx6x35z6f">
        <HeroSection data-id="element-3" data-mp="33mylndx1wrup" />
        <SocialProofSection data-id="element-4" data-mp="38lrp0dimj2h5" />
        <StepsSection data-id="element-5" data-mp="38li8rhnds9on" />

        {/* Invite Only Mascot Section */}
        <section ref={sectionRef} className="bg-[#D4F57A] py-16 border-y-4 border-[#1A1A1A] relative overflow-hidden z-20" data-id="element-6" data-mp="3dkuuu06fhcq6">
          <style data-id="element-7" data-mp="nur8c681cizz">{`
            @keyframes tetris-drop {
              0% { transform: translateY(-150px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
            .tetris-pill {
              opacity: 0;
            }
            .tetris-pill.animate {
              animation: tetris-drop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            }
          `}</style>

          <div className="max-w-6xl mx-auto px-4 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-0 mt-8" data-id="element-8" data-mp="d3wax0x1p6qw">
            {/* Left Side Pills */}
            <div className="hidden lg:flex flex-col gap-6 items-end w-72 -mr-16 relative z-20" data-id="element-9" data-mp="3gkl6xsz04y2b">
              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56`} style={{
              animationDelay: '0.8s'
            }} data-id="element-10" data-mp="3bn2zy84joswy">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is my data safe?'
                }));
              }} className="bg-[#FF90E8] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[6deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-11" data-mp="3lk7k62etyeb4">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-12" data-mp="1w33oajlvh1ab">
                    Is my data safe?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 mr-4`} style={{
              animationDelay: '0.6s'
            }} data-id="element-13" data-mp="1i5nhh6znz5i1">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Invite-only club'
                }));
              }} className="bg-[#4DE1C1] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[6deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-14" data-mp="6qs5xtwbdk5p">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-15" data-mp="zw0mxbljm0eb">
                    Wait, how do I get an invite?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56 mr-14`} style={{
              animationDelay: '0.4s'
            }} data-id="element-16" data-mp="3koubf1ihda52">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is this too good to be true?'
                }));
              }} className="bg-[#FFFC00] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[3deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-17" data-mp="1w5zwo9s28ojc">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-18" data-mp="tivc8ty7ye8n">
                    Is this too good to be true?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 mr-12`} style={{
              animationDelay: '0.2s'
            }} data-id="element-19" data-mp="21qslshun4eky">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Truly Automatic'
                }));
              }} className="bg-[#B28DFF] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[4deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-20" data-mp="2atbjyonhe30v">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-21" data-mp="3i33e37jag6s8">
                    How do I redeem?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-72 mr-10`} style={{
              animationDelay: '0s'
            }} data-id="element-22" data-mp="3n0dcvdskhusq">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Chill with Friends'
                }));
              }} className="bg-[#FFB067] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[2deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-23" data-mp="3d6czypr1nl9g">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-24" data-mp="2vx27hr1raaio">
                    Can I invite my friends?
                  </p>
                </div>
              </div>
            </div>

            {/* Center Content */}
            <div className="w-full max-w-sm flex flex-col items-center justify-center relative z-10 shrink-0" data-id="element-25" data-mp="2hwj7963j88r2">
              <ElephantMascot size="md" direction="right" className="mb-6 hover:scale-110 transition-transform" data-id="element-26" data-mp="2d59hm5hzh5f6" />
              <div className="bg-[#1A1A1A] rounded-3xl p-8 text-white relative shadow-[8px_8px_0px_0px_rgba(0,0,0,0.2)] w-full" data-id="element-27" data-mp="284dhm30r63f1">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-b-[16px] border-b-[#1A1A1A]" data-id="element-28" data-mp="1y8s69e9snqsm" />
                <h3 className="text-3xl font-black mb-4 font-display tracking-tight" data-id="element-29" data-mp="9pfo95yanqph">
                  This is an exclusive club.
                </h3>
                <p className="text-lg mb-8 text-gray-300" data-id="element-30" data-mp="dooiebx8xqlk">
                  Kraya is currently invite-only. Get an invite code from a
                  friend or join the waitlist to get early access.
                </p>
                {showWaitlistMessage ? <div className="bg-[#2A2A2A] rounded-xl p-4 text-center animate-fade-in border-2 border-[#D4F57A]" data-id="element-31" data-mp="3m7cf263k60q8">
                    <p className="text-[#D4F57A] font-medium" data-id="element-32" data-mp="3r5xgg8ujqqg9">
                      Stay tuned for our launch, please join our waitlist
                      meanwhile at{' '}
                      <a href="https://kraya.live" target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-white transition-colors" data-id="element-33" data-mp="4quw7tt74dc4">
                        kraya.live
                      </a>
                    </p>
                  </div> : <Button variant="primary" className="w-full text-lg py-4" onClick={() => setShowWaitlistMessage(true)} data-id="element-34" data-mp="2k4wytmgjdden">
                    Get Invite Code
                  </Button>}
              </div>
            </div>

            {/* Right Side Pills */}
            <div className="hidden lg:flex flex-col gap-6 items-start w-72 -ml-16 relative z-20" data-id="element-35" data-mp="21fqfbx7x4hrs">
              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56`} style={{
              animationDelay: '0.9s'
            }} data-id="element-36" data-mp="26etgh6ks23uh">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Gift an Experience'
                }));
              }} className="bg-[#FFFC00] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[3deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-37" data-mp="1dq5vzd7urihz">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-38" data-mp="3883azt90gq5r">
                    Can I gift perks?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 ml-4`} style={{
              animationDelay: '0.7s'
            }} data-id="element-39" data-mp="2zaszc6sa6z0b">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: "My favorite café isn't on the list"
                }));
              }} className="bg-[#B28DFF] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[8deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-40" data-mp="2pblk1e1zkuak">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-41" data-mp="2tlo6jtenvabx">
                    I don't see my fav cafe?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 ml-8`} style={{
              animationDelay: '0.5s'
            }} data-id="element-42" data-mp="3m4jxigrd4uep">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is my payment information secure?'
                }));
              }} className="bg-[#FF90E8] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[4deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-43" data-mp="15oevajnr1bwz">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-44" data-mp="282vpf8h9a2b3">
                    Is my payment secure?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 ml-2`} style={{
              animationDelay: '0.3s'
            }} data-id="element-45" data-mp="mleezd5ajo03">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'When will it be in my city?'
                }));
              }} className="bg-[#FFB067] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[5deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-46" data-mp="vn48coiqgx0i">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-47" data-mp="1tgdk5ecu4lyx">
                    When will it be in my city?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 ml-10`} style={{
              animationDelay: '0.1s'
            }} data-id="element-48" data-mp="1oiu54c8bc55x">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Surprise Perks'
                }));
              }} className="bg-[#45EBA5] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[2deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-49" data-mp="2c6li7i1ogm89">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-50" data-mp="jg1fbeeiydlz">
                    What else can I expect?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SplitSection data-id="element-51" data-mp="fgiqw8nhvkee" />
        <DifferenceSection data-id="element-52" data-mp="fgske67r09ou" />
        <WaitlistSection data-id="element-53" data-mp="fi3ns4xa7fff" />
      </main>
      <Footer data-id="element-54" data-mp="j9omjwnkw7w" />
    </div>;
}